import type {
  AnalysisCompletionRoot,
  AnalysisReply,
  AnalysisProgress,
  Lease,
  LocalVisualTestPrepareInput,
  AnalysisChunkDescriptor,
} from '@playcastdotio/Messaging';
import type { INativeMacroAnalysisClient } from '../macroAnalysis/INativeMacroAnalysisClient';

const LEASE_MS = 20_000;
const RENEW_MS = 8_000;
const ANALYSIS_TIMEOUT_MS = 35 * 60_000;
/** GPU progress can already be 100% while the completion object is still in flight. */
export const COMPLETION_STALL_MS = 15_000;

function calibrationCountsFinished(progress: AnalysisProgress): boolean {
  return progress.casesTotal > 0 && progress.casesDone === progress.casesTotal
    && progress.expectedBindings > 0 && progress.completedBindings === progress.expectedBindings;
}

interface ActiveRun { lease: Lease; dispose(): void }
export type LocalAnalysisRunInput = Pick<LocalVisualTestPrepareInput,
  'testId' | 'manifest' | 'manifestDigest' | 'workPlanDigest' | 'assetGrantEndpoint' | 'assetToken'>
  & { preparation?: Pick<AnalysisProgress, 'target' | 'expectedBindings' | 'casesTotal'> };

/** Owns one direct native analysis lease for an ephemeral local test. */
export class LocalVisualTestAnalysisRunner {
  private active: ActiveRun | null = null;

  public constructor(
    private readonly native: INativeMacroAnalysisClient,
    private readonly newId: () => string = () => crypto.randomUUID(),
    private readonly epoch: () => number = () => Date.now(),
  ) {}

  public run(test: LocalAnalysisRunInput, onProgress?: (progress: AnalysisProgress) => void,
    onChunk?: (chunk: AnalysisChunkDescriptor) => void): Promise<AnalysisCompletionRoot> {
    if (this.active) return Promise.reject(new Error('visual_test_analysis_busy'));
    if (this.native.surface().running !== 'idle') return Promise.reject(new Error('visual_test_gpu_worker_busy'));
    const requestId = this.newId();
    const lease: Lease = { jobId: test.testId, manifestDigest: test.manifestDigest,
      workPlanDigest: test.workPlanDigest, hostId: 'local-visual-test', socketId: test.testId,
      epoch: this.epoch(), renewalSequence: 0, remainingMs: LEASE_MS };
    this.native.noteAssignment('calibration', false);
    return new Promise((resolve, reject) => {
      let settled = false;
      let stall: ReturnType<typeof setTimeout> | undefined;
      const finish = (error?: Error, completion?: AnalysisCompletionRoot) => {
        if (settled) return;
        settled = true; unsubscribe(); clearInterval(renewal); clearTimeout(timeout); clearTimeout(stall);
        this.active = null; this.native.noteAssignment('idle', false);
        if (error) reject(error);
        else if (completion) resolve(completion);
        else reject(new Error('visual_test_analysis_completion_missing'));
      };
      const armStall = (progress: AnalysisProgress) => {
        if (stall || !calibrationCountsFinished(progress)) return;
        stall = setTimeout(() => {
          void this.native.send({ command: 'analysisCancel', requestId: this.newId(), lease: { ...lease } }).catch(() => undefined);
          finish(new Error('visual_test_completion_dropped'));
        }, COMPLETION_STALL_MS);
      };
      const onReply = (reply: AnalysisReply) => {
        if (!('lease' in reply) || reply.requestId !== requestId
          || !(['jobId', 'manifestDigest', 'workPlanDigest', 'hostId', 'socketId', 'epoch'] as const)
            .every(field => reply.lease[field] === lease[field])) return;
        if (reply.status === 'preparing' && test.preparation) onProgress?.({ ...test.preparation,
          jobId: lease.jobId, manifestDigest: lease.manifestDigest, status: 'preparing', casesDone: 0, completedBindings: 0 });
        if (reply.progress) { onProgress?.(reply.progress); armStall(reply.progress); }
        if (reply.chunk) onChunk?.(reply.chunk);
        if (reply.status === 'completed') {
          if (!reply.completion) finish(new Error('visual_test_analysis_completion_missing'));
          else finish(undefined, reply.completion);
        } else if (reply.status === 'failed' || reply.status === 'cancelled') {
          finish(new Error(reply.errorCode ?? `visual_test_analysis_${reply.status}`));
        }
      };
      const unsubscribe = this.native.subscribe(onReply);
      const renewal = setInterval(() => {
        lease.renewalSequence += 1;
        void this.native.send({ command: 'analysisRenew', requestId: this.newId(), lease: { ...lease } })
          .catch(() => finish(new Error('visual_test_analysis_renew_failed')));
      }, RENEW_MS);
      const timeout = setTimeout(() => {
        void this.native.send({ command: 'analysisCancel', requestId: this.newId(), lease: { ...lease } }).catch(() => undefined);
        finish(new Error('visual_test_analysis_timeout'));
      }, ANALYSIS_TIMEOUT_MS);
      this.active = { lease, dispose: () => finish(new Error('visual_test_analysis_cancelled')) };
      void this.native.send({ command: 'analysisStart', requestId, lease: { ...lease }, manifest: test.manifest,
        acknowledged: [], assetGrantEndpoint: test.assetGrantEndpoint, assetToken: test.assetToken })
        .catch(() => finish(new Error('visual_test_analysis_start_failed')));
    });
  }

  public cancel(): void {
    const active = this.active;
    if (!active) return;
    void this.native.send({ command: 'analysisCancel', requestId: this.newId(), lease: { ...active.lease } }).catch(() => undefined);
    active.dispose();
  }
}
